import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
// The same six cube faces, converted to WebP at 2048x2048 in place. Measured: 112.9 MB of
// PNG became 3.6 MB on disk, and the decoded GPU cost falls from ~403 MB to ~101 MB. The
// names, order and the urls array below are unchanged.
import px from '../../../Assets/Images/SpaceCubeMap/px.webp';
import nx from '../../../Assets/Images/SpaceCubeMap/nx.webp';
import py from '../../../Assets/Images/SpaceCubeMap/py.webp';
import ny from '../../../Assets/Images/SpaceCubeMap/ny.webp';
import pz from '../../../Assets/Images/SpaceCubeMap/pz.webp';
import nz from '../../../Assets/Images/SpaceCubeMap/nz.webp';

import { BokehDepthShader } from 'three/examples/jsm/shaders/BokehShader2.js'
import { getLoadingManager } from '../../Utils/loadManager';
import {
    diagonalHomePosition,
    LOBBY_CAMERA_START,
    SUN_HOME_YAW,
} from '../../Utils/cameraHome';

const urls = [
    px, nx,
    py, ny,
    pz, nz
];
const depthShader = BokehDepthShader;

const homePosition = diagonalHomePosition();

// The camera's home: centred between the +X and +Z axes, at the same distance
// from the sun it has always been, so the flight in from the gate lands looking
// straight at the sun. Also where the planet-focus move returns to.
export const mainCameraInitPosition = new THREE.Vector3(homePosition.x, homePosition.y, homePosition.z);
export const mainCameraInitRotation = new THREE.Euler(0, SUN_HOME_YAW, 0);
const spotlight = new THREE.SpotLight();


export const scene = new THREE.Scene();
export const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
export let h;
export const rig = new THREE.Group();
export let cameraControls;

let canvasEl;
let activeCamera;
let aspect = null;

export const MODEL = {
    camera: null,
    canvasEl: null,
    scene: null,
    renderer: null,
    prevClip: "",
    x: 0,
    y: 0,
    animations: {
        mixer: null,
        aboutMe: {
            clips: [],
            actions: []
        },
        web: {
            clips: [],
            actions: []
        },
        vr: {
            clips: [],
            actions: []
        }
    }
};

const camera = new THREE.PerspectiveCamera();

export function loadMainScene() {

    scene.name = 'mainScene';
    scene.fog = null;
    canvasEl = document.getElementById('canvasEl');
    aspect = canvasEl.clientWidth / canvasEl.clientHeight;

    // Set before setSize, because setSize multiplies by the pixel ratio to size the drawing
    // buffer. Without this line the ratio is three's default of 1, so the canvas is the CSS
    // size and the browser upscales it — the whole scene is soft on every retina screen and
    // phone. The cap of 2 is deliberate: an uncapped 3x phone screen would quadruple the
    // fragment work again (nine times 1x), and this scene is being tuned for smoothness on
    // weak devices, so sharpness past 2 is not worth the fill rate.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // These are CSS pixels, which is the correct argument once a pixel ratio is set above —
    // setSize does the multiply itself.
    renderer.setSize(canvasEl.clientWidth, canvasEl.clientHeight);

    // renderer.encoding and renderer.outputEncoding were assigned `true` here until now.
    // Neither assignment ever did anything: `encoding` has never existed on WebGLRenderer,
    // and `outputEncoding` does not exist in this project's three (r175) at all — grepping
    // three.module.js for it returns nothing — so both lines only added two properties to
    // the renderer object that nothing reads. No replacement is needed: outputColorSpace
    // already defaults to THREE.SRGBColorSpace, which is the sRGB output this scene wants.

    // Measured, and visually identical. Nothing in this scene casts a shadow — the only
    // castShadow = true assignments anywhere in src/ are on lights (the camera SpotLight
    // below, the sun PointLight in Planets.js), and no mesh sets it — so every shadow map
    // was empty and disabling them changes nothing that is drawn. The saving is per-frame
    // work: a PointLight that casts renders a six-face cube shadow map, one per face, plus
    // one for the SpotLight, so seven shadow passes and their render-target clears each
    // frame, all of them drawing zero objects. The type was costing more than the passes —
    // PCFSoftShadowMap compiles soft-shadow sampling into every lit material's fragment
    // shader, where it was then evaluated against an empty map.
    // This line is the one to turn back on if a caster is ever added: three meshes already
    // receive (planet_mesh, Saturn's ring, and the text labels in Planets.js).
    renderer.shadowMap.enabled = false;

    canvasEl.appendChild(renderer.domElement);

    //ADD CAMERAS AND LIGHTS
    loadCameras();

    //setGui();

    setActiveCamera(camera);

    loadCubeMap();

    MODEL.scene = scene;
    MODEL.camera = camera;
    MODEL.canvasEl = canvasEl;
    MODEL.renderer = renderer;
}

function loadCameras() {
    rig.name = 'Camera Rig'
    rig.position.set(0, 0, 0);

    // far has to clear the sun seen from the gate 10 000 units out, or the whole
    // system is clipped at the start. near is three.js's own default, and low enough that
    // the two smallest bodies can be framed at all: at a near of 1, the clamp in
    // landingDistance held the camera 1.1 clear of the surface, which parked it so far back
    // that Mercury and Mars spanned barely half the frame and two thirds of it. The price
    // is depth precision far from the camera, where nothing overlaps.
    camera.copy(new THREE.PerspectiveCamera(35, aspect, 0.1, 20000));
    camera.name = 'mainCamera';
    camera.position.set(LOBBY_CAMERA_START.x, LOBBY_CAMERA_START.y, LOBBY_CAMERA_START.z);
    camera.layers.enable(1);

    // Layer 5 is the planet labels (Planets.js, createText). They sit on a layer of their
    // own so the raycasts leave them alone — every raycaster runs on layer 0, and a label
    // in front of a planet would otherwise swallow the click that selects it. The other
    // half of that trade is this line: the renderer draws only what the camera's own mask
    // matches, so a label on a layer the camera does not carry is culled before its
    // position, scale, material or font is ever consulted.
    camera.layers.enable(5);

    // Layer 6 is the corner logo (3DWebpage/CornerLogo). Same trade as layer 5 above, and it
    // needs it for the same reason: `raycastFromCentre` is called with `scene.children` and
    // walks the subtree, so the coin would otherwise be a target of the lobby's own aim.
    camera.layers.enable(6);

    // Layer 7 is the category row (3DWebpage/NavBar). The same two halves as 5 and 6 above, with
    // one addition: the row is the first HUD that is *meant* to be clicked. So it is drawn, and it
    // is out of reach of every raycaster in the project, and the pointer that does have to reach
    // it is the row's own — masked to 7 and 6, in NavBar.
    camera.layers.enable(7);

    rig.add(camera);

    scene.add(rig);

    setActiveCamera(camera);

    setCameraControls();

    console.log("my rig ", rig);
    loadLighting(camera);
}



function loadLighting() {
    const ambient = new THREE.HemisphereLight(0xffffff, 0x8d8d8d, 0.1);
    scene.add(ambient);

    spotlight.distance = 100;
    spotlight.intensity = 8;
    spotlight.decay = 1;
    spotlight.penumbra = 1;
    spotlight.focus = 1;
    spotlight.name = 'cameraSpotLight';
    spotlight.castShadow = true;
    spotlight.position.y -= 1;

    camera.add(spotlight);

    // No angle written here on purpose. It is the one property of this light that is not a taste
    // but a fit, and the frame it has to fit is not known until it is asked for — see
    // fitSpotlightToCamera below.
    fitSpotlightToCamera();
}

/**
 * Sizes the camera's spotlight to the largest cone the frame can hold — the widest beam whose own
 * edge is still on screen.
 *
 * This light is the lobby's flashlight, and its cone is written in world angles, so how much of the
 * *screen* it fills is decided by the viewport rather than by anything on the light. The 0.5 radian
 * (28.6 degree) cone this used to be fixed at matched a 1280x720 frame's 29.3-degree horizontal
 * half-angle almost exactly, which is why it read as a beam there. Every other frame it did not
 * match: a phone held upright is 390x844, a horizontal half-angle of 8.3 degrees, so the same cone
 * overran the width more than three times over and its falloff happened off screen. Nothing about
 * the light had changed; there was simply none of it left in the frame to see.
 *
 * The fix is to consult the camera rather than to keep a number, but *which* half-angle is the whole
 * of it. Tracking the horizontal alone is right on a portrait frame — where the horizontal is also
 * the shorter axis, 8.3 of a 17.5-degree vertical — and wrong the moment the phone is turned: a
 * 844x390 frame has a 34.3-degree horizontal half-angle against a 17.5-degree vertical, so a cone
 * cut to the width now stands taller than the frame. Its boundary leaves the screen at the top and
 * bottom, the falloff has nothing to fall off over, and the flashlight is gone again — with the
 * frame's own aspect as the only thing that changed.
 *
 * So the angle is taken from the *narrower* of the two half-angles. That is the vertical on any
 * frame wider than it is tall and the horizontal on any frame taller than it is wide, and taking it
 * is the same statement as "the beam's edge lands on the frame's nearest edge" in both cases. It is
 * also the largest circle that fits inside the rectangle, which is what makes it the most light
 * available without the edge moving off screen.
 *
 * The cost is on the wide frame, which is the one frame that used to be right: 1280x720 goes from a
 * 28.6-degree cone to a 17.5-degree one, so the lobby's left and right thirds darken where they used
 * to be lit. That is the same beam getting a bounded edge rather than a change of intent, but it is
 * a visible change on the desktop and the one thing here worth a look rather than an argument. If it
 * wants to sit further in again, this line is where — a fraction of the value below.
 */
export function fitSpotlightToCamera() {
    const halfVertical = THREE.MathUtils.degToRad(camera.fov) / 2;
    const halfHorizontal = Math.atan(Math.tan(halfVertical) * camera.aspect);

    spotlight.angle = Math.min(halfVertical, halfHorizontal);
}

export function setActiveCamera(cam = null) {
    ///activeCamera.visible = false;
    if (cam) {
        activeCamera = cam;
    } else {

        if (getActiveCamera().name !== 'mainCamera') {
            activeCamera = rig.getObjectByName('newCamera');
            cameraControls.enabled = false;
        }
        else {
            console.log("new Camera to be added");
        }
    }
}

export function loadCubeMap() {
    const camera = getActiveCamera();
    const materialDepth = new THREE.ShaderMaterial({
        uniforms: depthShader.uniforms,
        vertexShader: depthShader.vertexShader,
        fragmentShader: depthShader.fragmentShader
    })
    materialDepth.uniforms['mNear'].value = camera.near;
    materialDepth.uniforms['mFar'].value = camera.far;

    const textureCube = new THREE.CubeTextureLoader(getLoadingManager()).load(urls);
    console.log(textureCube);

    scene.background = textureCube;

    console.log(scene);
}

export function setCameraControls() {
    // OrbitControls
    cameraControls = new OrbitControls(getActiveCamera(), renderer.domElement);
    cameraControls.dragToLook = true;
    cameraControls.movementSpeed = 100;
    cameraControls.rollSpeed = 0.3;
    cameraControls.enabled = true;

}

export function getActiveCamera() {
    return activeCamera;
}

export function getSpotlight() {
    return spotlight;
}

export function setSpotlightParams(params) {
    if (params) {
        Object.keys(params).forEach((p) => {
            spotlight.intensity = p === 'intensity' ? params[p] : spotlight.intensity;
            spotlight.distance = p === 'distance' ? params[p] : spotlight.distance;
            spotlight.angle = p === 'angle' ? params[p] : spotlight.angle;
        })
    }
}

// GUI
/* function setGui() {
    const gui = new GUI();

    const params = {
        color: spotlight.color.getHex(),
        intensity: spotlight.intensity,
        distance: spotlight.distance,
        angle: spotlight.angle,
        penumbra: spotlight.penumbra,
        decay: spotlight.decay,
        focus: spotlight.shadow.focus,
        shadows: true        
    };

    gui.addColor(params, 'color').onChange(function (val) {

        spotlight.color.setHex(val);

    });

    gui.add(params, 'intensity', 0, 500).onChange(function (val) {

        spotlight.intensity = val;

    });


    gui.add(params, 'distance', 0, 200).onChange(function (val) {

        spotlight.distance = val;

    });

    gui.add(params, 'angle', 0, Math.PI / 3).onChange(function (val) {

        spotlight.angle = val;

    });

    gui.add(params, 'penumbra', 0, 1).onChange(function (val) {

        spotlight.penumbra = val;

    });

    gui.add(params, 'decay', 1, 2).onChange(function (val) {

        spotlight.decay = val;

    });

    gui.add(params, 'focus', 0, 1).onChange(function (val) {

        spotlight.shadow.focus = val;

    });


    gui.add(params, 'shadows').onChange(function (val) {
        renderer.shadowMap.enabled = val;
        scene.traverse(function (child) {
            if (child.material) {
                child.material.needsUpdate = true;
            }
        });
    });

    gui.open();
} */

